/**
 * Los hooks de datos de compras.
 *
 * Viven aca y no en `lib/queries.ts` por la misma razon que los de listas:
 * son de este modulo, contra `api` de `lib/api.ts`, que sigue siendo la unica
 * puerta al backend. La subida es la excepcion (XHR, para tener progreso).
 *
 * Aplicar o revertir invalida productos, stock y precios: `aplicar_compra()`
 * suma stock y reescribe costos, y el trigger recalcula `precio_venta`. Un
 * precio cacheado despues de aplicar es un precio que ya no existe.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, desenvolver, ErrorApi, URL_API } from "@/lib/api";
import { claves } from "@/lib/queries";
import type { Moneda, Redondeo } from "@tora/shared";
import type { Proveedor } from "@/lib/tipos";

import type {
  AccionFilaCompra,
  Compra,
  EstadoCompra,
  FilaCompra,
  ResultadoAplicarCompra,
  ResultadoRevertirCompra,
  ResumenMatchingCompra,
  TipoComprobante,
} from "./logica";

const TIMEOUT_LARGO = 60_000;
const INTERVALO_POLLING = 1_500;

export const clavesCompras = {
  raiz: ["compras"] as const,
  lista: ["compras", "lista"] as const,
  compra: (id: string) => ["compras", "una", id] as const,
  revision: (id: string) => ["compras", "revision", id] as const,
};

export interface RespuestaCompra {
  compra: Compra;
  resumen: ResumenMatchingCompra | null;
}

export interface RespuestaRevision {
  compra: Compra;
  filas: FilaCompra[];
  /** El margen habitual de cada rubro (0148), para proponer el de un producto nuevo. */
  margenes?: Array<{ rubro_id: number; margen: number }>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Historial
// ─────────────────────────────────────────────────────────────────────────────

export function useCompras(): UseQueryResult<{ compras: Compra[] }, ErrorApi> {
  return useQuery<{ compras: Compra[] }, ErrorApi>({
    queryKey: clavesCompras.lista,
    queryFn: ({ signal }) => api.get<{ compras: Compra[] }>("/compras", { signal }),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Subida
// ─────────────────────────────────────────────────────────────────────────────

export interface DatosSubida {
  archivo: File;
  onProgreso?: (pct: number) => void;
}

export interface RespuestaSubida {
  compra_id: string;
  estado: EstadoCompra;
}

function leerCuerpo(texto: string): unknown {
  if (texto === "") return null;
  try {
    return JSON.parse(texto) as unknown;
  } catch {
    return texto;
  }
}

function mensajeDeCuerpo(cuerpo: unknown, estado: number): string {
  if (typeof cuerpo === "object" && cuerpo !== null) {
    const error = (cuerpo as { error?: unknown }).error;
    if (typeof error === "string" && error.trim() !== "") return error;
  }
  if (typeof cuerpo === "string" && cuerpo.trim() !== "") return cuerpo;
  return `El servidor respondió ${estado} sin explicación.`;
}

/** Sube el archivo con XHR: es lo unico que informa el progreso de subida. */
export function subirCompra({ archivo, onProgreso }: DatosSubida): Promise<RespuestaSubida> {
  return new Promise((resolver, rechazar) => {
    const cuerpo = new FormData();
    cuerpo.append("archivo", archivo);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${URL_API}/compras`);
    xhr.withCredentials = true;

    if (onProgreso) {
      xhr.upload.addEventListener("progress", (evento) => {
        if (evento.lengthComputable && evento.total > 0) {
          onProgreso(Math.min(100, Math.round((evento.loaded / evento.total) * 100)));
        }
      });
    }

    xhr.addEventListener("load", () => {
      const contenido = leerCuerpo(xhr.responseText);
      if (xhr.status >= 200 && xhr.status < 300) {
        const datos = desenvolver<RespuestaSubida>(contenido);
        if (datos && typeof datos.compra_id === "string") {
          resolver(datos);
          return;
        }
        rechazar(
          new ErrorApi({
            mensaje: "El servidor aceptó el archivo pero no devolvió la compra.",
            codigo: "RESPUESTA_INESPERADA",
            estado: xhr.status,
            cuerpo: contenido,
          }),
        );
        return;
      }
      rechazar(
        new ErrorApi({
          mensaje: mensajeDeCuerpo(contenido, xhr.status),
          estado: xhr.status,
          cuerpo: contenido,
        }),
      );
    });

    xhr.addEventListener("error", () => {
      rechazar(
        new ErrorApi({
          mensaje: "No pudimos conectarnos con el servidor. Revisá que el sistema esté corriendo.",
          codigo: "SIN_CONEXION",
          estado: 0,
        }),
      );
    });

    xhr.addEventListener("abort", () => {
      rechazar(new ErrorApi({ mensaje: "Se canceló la subida.", codigo: "CANCELADO", estado: 0 }));
    });

    xhr.send(cuerpo);
  });
}

export function useSubirCompra(): UseMutationResult<RespuestaSubida, ErrorApi, DatosSubida> {
  const qc = useQueryClient();
  return useMutation<RespuestaSubida, ErrorApi, DatosSubida>({
    mutationFn: subirCompra,
    onSuccess: () => void qc.invalidateQueries({ queryKey: clavesCompras.lista }),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Sin remito
// ─────────────────────────────────────────────────────────────────────────────

export interface DatosCompraManual {
  proveedor_id: number;
  moneda: Moneda;
  fecha_comprobante?: string | null;
  tipo_comprobante?: TipoComprobante | null;
  numero_comprobante?: string | null;
  numero_remito?: string | null;
  /** El dolar que tomo el proveedor. Obligatorio si hay dolares en juego (0101). */
  cotizacion?: number | null;
}

/** Una compra sin archivo: nace COMPLETADA y vacia, las lineas van a mano. */
export function useCrearCompraManual(): UseMutationResult<
  { compra: Compra },
  ErrorApi,
  DatosCompraManual
> {
  const qc = useQueryClient();
  return useMutation<{ compra: Compra }, ErrorApi, DatosCompraManual>({
    mutationFn: (datos) => api.post<{ compra: Compra }>("/compras/manual", datos),
    onSuccess: () => void qc.invalidateQueries({ queryKey: clavesCompras.lista }),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Lectura
// ─────────────────────────────────────────────────────────────────────────────

/** Estado de la compra. Se repregunta sola mientras el backend lee el archivo. */
export function useCompra(id: string | null): UseQueryResult<RespuestaCompra, ErrorApi> {
  return useQuery<RespuestaCompra, ErrorApi>({
    queryKey: clavesCompras.compra(id ?? ""),
    queryFn: ({ signal }) =>
      api.get<RespuestaCompra>(`/compras/${id ?? ""}`, { signal, timeout: TIMEOUT_LARGO }),
    enabled: id !== null,
    refetchInterval: (consulta) => {
      const estado = consulta.state.data?.compra.estado;
      return estado === "PENDIENTE" || estado === "PROCESANDO" ? INTERVALO_POLLING : false;
    },
  });
}

export function useRevisionCompra(
  id: string | null,
  habilitada: boolean,
): UseQueryResult<RespuestaRevision, ErrorApi> {
  return useQuery<RespuestaRevision, ErrorApi>({
    queryKey: clavesCompras.revision(id ?? ""),
    queryFn: ({ signal }) =>
      api.get<RespuestaRevision>(`/compras/${id ?? ""}/revision`, {
        signal,
        timeout: TIMEOUT_LARGO,
      }),
    enabled: id !== null && habilitada,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Correcciones
// ─────────────────────────────────────────────────────────────────────────────

function invalidarCompra(qc: ReturnType<typeof useQueryClient>, id: string): void {
  void qc.invalidateQueries({ queryKey: clavesCompras.compra(id) });
  void qc.invalidateQueries({ queryKey: clavesCompras.revision(id) });
}

export interface CambiosEncabezado {
  proveedor_id?: number | null;
  tipo_comprobante?: TipoComprobante | null;
  numero_comprobante?: string | null;
  numero_remito?: string | null;
  fecha_comprobante?: string | null;
  moneda?: Moneda | null;
  cotizacion?: number | null;
  total_declarado?: number | null;
  total_a_pagar?: number | null;
  /** El importe que se tildo en «Cuánto le debo». Null lo destilda. */
  deuda_confirmada?: number | null;
  genera_deuda?: boolean;
}

/**
 * Cambiar el proveedor re-matchea las lineas del lado del backend, asi que la
 * revision se relee entera.
 */
export function useActualizarEncabezado(
  compraId: string | null,
): UseMutationResult<{ compra: Compra }, ErrorApi, CambiosEncabezado> {
  const qc = useQueryClient();
  return useMutation<{ compra: Compra }, ErrorApi, CambiosEncabezado>({
    mutationFn: (cambios) => api.patch<{ compra: Compra }>(`/compras/${compraId ?? ""}`, cambios),
    onSuccess: () => {
      if (compraId !== null) invalidarCompra(qc, compraId);
    },
  });
}

export interface CambiosFila {
  producto_id?: number | null;
  accion?: AccionFilaCompra;
  /** La cantidad misma: solo en una compra a mano (en un remito, 422). */
  cantidad?: number;
  cantidad_recibida?: number | null;
  motivo_diferencia?: string | null;
  /** El precio unitario sin IVA, corregido: no decide el destino de la linea. */
  costo_neto?: number;
  nuevo_nombre?: string | null;
  nuevo_rubro_id?: number | null;
  nuevo_codigo_interno?: string | null;
  nuevo_precio_manual?: number | null;
  nuevo_markup_pct?: number | null;
  /** 0153: el precio de venta revisado; tampoco decide el destino de la linea. */
  precio_elegido?: number | null;
  precio_markup?: number | null;
  precio_redondeo?: Redondeo | null;
}

/**
 * Lo que el operador decide sobre una linea. La fila que vuelve ya viene
 * releida de la vista (precio resultante, codigo sugerido), asi que se parchea
 * en la cache sin refetchear las demas.
 */
export function useActualizarFilaCompra(
  compraId: string | null,
): UseMutationResult<{ fila: FilaCompra }, ErrorApi, { filaId: number; cambios: CambiosFila }> {
  const qc = useQueryClient();
  return useMutation<{ fila: FilaCompra }, ErrorApi, { filaId: number; cambios: CambiosFila }>({
    mutationFn: ({ filaId, cambios }) =>
      api.patch<{ fila: FilaCompra }>(`/compras/${compraId ?? ""}/filas/${filaId}`, cambios),
    onSuccess: (respuesta, variables) => {
      if (compraId === null) return;
      qc.setQueryData<RespuestaRevision>(clavesCompras.revision(compraId), (viejo) => {
        if (!viejo) return viejo;
        return {
          ...viejo,
          filas: viejo.filas.map((f) => (f.id === variables.filaId ? respuesta.fila : f)),
        };
      });
    },
  });
}

export interface NuevaFila {
  /** Elegido del catalogo: la linea nace decidida y el wizard no repregunta. */
  producto_id?: number | null;
  codigo_externo?: string | null;
  descripcion: string;
  cantidad: number;
  costo_neto: number;
  iva_pct: number;
  moneda: Moneda;
}

/** Una linea a mano: la de una compra sin remito, o la salida cuando la extraccion fallo. */
export function useAgregarFilaCompra(
  compraId: string | null,
): UseMutationResult<{ fila: FilaCompra }, ErrorApi, NuevaFila> {
  const qc = useQueryClient();
  return useMutation<{ fila: FilaCompra }, ErrorApi, NuevaFila>({
    mutationFn: (fila) => api.post<{ fila: FilaCompra }>(`/compras/${compraId ?? ""}/filas`, fila),
    onSuccess: () => {
      if (compraId !== null) invalidarCompra(qc, compraId);
    },
  });
}

export function useEliminarFilaCompra(
  compraId: string | null,
): UseMutationResult<unknown, ErrorApi, number> {
  const qc = useQueryClient();
  return useMutation<unknown, ErrorApi, number>({
    mutationFn: (filaId) => api.delete(`/compras/${compraId ?? ""}/filas/${filaId}`),
    onSuccess: () => {
      if (compraId !== null) invalidarCompra(qc, compraId);
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Aplicar y revertir
// ─────────────────────────────────────────────────────────────────────────────

function invalidarPorStock(qc: ReturnType<typeof useQueryClient>, compraId: string): void {
  for (const clave of [
    clavesCompras.compra(compraId),
    clavesCompras.revision(compraId),
    clavesCompras.lista,
    claves.productos,
    claves.stock,
    claves.precios,
  ]) {
    void qc.invalidateQueries({ queryKey: [...clave] });
  }
}

/** El pago que puede acompañar al aplicar: la compra que ya venía paga. */
export interface PagoDeCompra {
  pagos: Array<{
    cuenta_id: number;
    medio: string;
    importe: number;
    moneda_deuda?: Moneda;
    cotizacion?: number;
  }>;
}

/**
 * Aplicar, con el pago opcional.
 *
 * Invalida además `proveedores`: aplicar asienta la deuda, y si no se invalida
 * la lista sigue mostrando el saldo de antes hasta que alguien recargue.
 */
/** Lo que viaja al aplicar: el pago, si ya estaba paga, y los encargos tildados. */
export interface DatosAplicarCompra {
  pago?: PagoDeCompra;
  encargos?: ReadonlyArray<{ encargo_id: number; fila_id: number }>;
}

export function useAplicarCompra(
  compraId: string | null,
): UseMutationResult<ResultadoAplicarCompra, ErrorApi, DatosAplicarCompra> {
  const qc = useQueryClient();
  return useMutation<ResultadoAplicarCompra, ErrorApi, DatosAplicarCompra>({
    mutationFn: ({ pago, encargos }) =>
      api.post<ResultadoAplicarCompra>(
        `/compras/${compraId ?? ""}/aplicar`,
        {
          ...(pago === undefined ? {} : { pago }),
          ...(encargos && encargos.length > 0 ? { encargos } : {}),
        },
        { timeout: TIMEOUT_LARGO },
      ),
    onSuccess: () => {
      if (compraId !== null) invalidarPorStock(qc, compraId);
      void qc.invalidateQueries({ queryKey: claves.proveedores });
      void qc.invalidateQueries({ queryKey: claves.cajas });
      void qc.invalidateQueries({ queryKey: claves.encargos });
      void qc.invalidateQueries({ queryKey: claves.ventas });
    },
  });
}

export function useRevertirCompra(
  compraId: string | null,
): UseMutationResult<ResultadoRevertirCompra, ErrorApi, void> {
  const qc = useQueryClient();
  return useMutation<ResultadoRevertirCompra, ErrorApi, void>({
    mutationFn: () =>
      api.post<ResultadoRevertirCompra>(`/compras/${compraId ?? ""}/revertir`, undefined, {
        timeout: TIMEOUT_LARGO,
      }),
    onSuccess: () => {
      if (compraId !== null) invalidarPorStock(qc, compraId);
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Descartar
// ─────────────────────────────────────────────────────────────────────────────

/** La compra se cargo por error o se abandono. No toca stock: nunca lo toco. */
export function useDescartarCompra(
  compraId: string | null,
): UseMutationResult<{ compra: Compra }, ErrorApi, void> {
  const qc = useQueryClient();
  return useMutation<{ compra: Compra }, ErrorApi, void>({
    mutationFn: () => api.post<{ compra: Compra }>(`/compras/${compraId ?? ""}/descartar`),
    onSuccess: () => {
      if (compraId !== null) invalidarCompra(qc, compraId);
      void qc.invalidateQueries({ queryKey: clavesCompras.lista });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Proveedor nuevo
// ─────────────────────────────────────────────────────────────────────────────

export interface NuevoProveedor {
  nombre: string;
  moneda_lista?: Moneda;
}

export function useCrearProveedor(): UseMutationResult<Proveedor, ErrorApi, NuevoProveedor> {
  const qc = useQueryClient();
  return useMutation<Proveedor, ErrorApi, NuevoProveedor>({
    mutationFn: (datos) => api.post<Proveedor>("/proveedores", datos),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...claves.proveedores] }),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Memoria de la compra en curso
// ─────────────────────────────────────────────────────────────────────────────

const CLAVE_MEMORIA = "tora.compras.actual";

/** El id de la compra a medias queda en el navegador: un F5 no la pierde. */
export function recordarCompra(id: string | null): void {
  try {
    if (id === null) localStorage.removeItem(CLAVE_MEMORIA);
    else localStorage.setItem(CLAVE_MEMORIA, id);
  } catch {
    // Modo privado o storage lleno: se pierde la memoria, no la pantalla.
  }
}

export function compraRecordada(): string | null {
  try {
    const guardado = localStorage.getItem(CLAVE_MEMORIA);
    return guardado === null || guardado === "" ? null : guardado;
  } catch {
    return null;
  }
}
